import { useEffect, useRef, ReactNode } from 'react';
import maplibregl, { Map, MapOptions, NavigationControl, GeolocateControl, FullscreenControl } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapContext } from './MapContext';

export interface MapLibreMapProps {
  center?: [number, number];
  zoom?: number;
  style?: string;
  accessToken?: string;
  children?: ReactNode;
  onMapLoad?: (map: Map) => void;
  onMapClick?: (lng: number, lat: number) => void;
  className?: string;
  showControls?: boolean;
  showGeolocate?: boolean;
  showFullscreen?: boolean;
}

export const MapLibreMap = ({
  center = [35.3308, 36.9914],
  zoom = 13,
  style,
  accessToken,
  children,
  onMapLoad,
  onMapClick,
  className = '',
  showControls = true,
  showGeolocate = true,
  showFullscreen = true,
}: MapLibreMapProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  const onMapLoadRef = useRef(onMapLoad);
  const onMapClickRef = useRef(onMapClick);
  const initialCenterRef = useRef(center);
  const initialZoomRef = useRef(zoom);

  useEffect(() => {
    onMapLoadRef.current = onMapLoad;
  }, [onMapLoad]);

  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);

  useEffect(() => {
    if (!mapContainerRef.current) {
      return;
    }

    const mapboxToken = accessToken || import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
    
    const fallbackStyleUrl = 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json';
    
    let styleUrl = style;
    if (!styleUrl) {
      styleUrl = fallbackStyleUrl;
    }

    const mapOptions: MapOptions = {
      container: mapContainerRef.current,
      style: styleUrl,
      center: initialCenterRef.current,
      zoom: initialZoomRef.current,
    };

    const map = new maplibregl.Map(mapOptions);

    map.on('load', () => {
      if (onMapLoadRef.current) {
        onMapLoadRef.current(map);
      }
    });

    map.on('error', (e) => {
      console.error('MapLibre error:', e);
      if (e.error && e.error.message && e.error.message.includes('unknown property')) {
        console.warn('Mapbox style compatibility issue detected, falling back to Carto Voyager');
        map.setStyle(fallbackStyleUrl).catch((err) => {
          console.error('Failed to set fallback style:', err);
        });
      }
    });

    map.on('styledata', () => {
      if (map.isStyleLoaded()) {
        if (onMapLoadRef.current) {
          onMapLoadRef.current(map);
        }
      }
    });

    if (onMapClickRef.current) {
      map.on('click', (e) => {
        if (onMapClickRef.current) {
          onMapClickRef.current(e.lngLat.lng, e.lngLat.lat);
        }
      });
    }

    if (showControls) {
      map.addControl(new NavigationControl(), 'top-right');
    }

    if (showGeolocate) {
      const geolocate = new GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
      });
      map.addControl(geolocate, 'top-right');
    }

    if (showFullscreen) {
      map.addControl(new FullscreenControl(), 'top-right');
    }

    mapRef.current = map;

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [style, accessToken, showControls, showGeolocate, showFullscreen]);

  useEffect(() => {
    if (mapRef.current && center && mapRef.current.isStyleLoaded()) {
      const currentCenter = mapRef.current.getCenter();
      const centerChanged = 
        Math.abs(currentCenter.lng - center[0]) > 0.0001 ||
        Math.abs(currentCenter.lat - center[1]) > 0.0001;
      
      if (centerChanged) {
        mapRef.current.setCenter(center);
      }
    }
  }, [center]);

  useEffect(() => {
    if (mapRef.current && zoom !== undefined && mapRef.current.isStyleLoaded()) {
      const currentZoom = mapRef.current.getZoom();
      if (Math.abs(currentZoom - zoom) > 0.1) {
        mapRef.current.setZoom(zoom);
      }
    }
  }, [zoom]);

  return (
    <div ref={mapContainerRef} className={`w-full h-full ${className}`}>
      <MapContext.Provider value={mapRef.current || null}>
        {children}
      </MapContext.Provider>
    </div>
  );
};
